import { ColDef, ColumnState, GridApi, GridReadyEvent, Module } from '@ag-grid-community/core';
import { Component, EventEmitter, HostBinding, Injector, Input, OnChanges, OnDestroy, Output, SimpleChanges }
	from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { NgIf } from '@angular/common';
import { AgGridModule } from '@ag-grid-community/angular';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';

import { LoadingSpinnerCellComponent } from '../../../shared/datacenter/cell-renderers/loading-spinner-cell.component';
import { clientSideAgGridModules, CommonGridOptions } from '../../../shared/datacenter/grid-options';
import { DatacenterType, UserDatacenterView } from '../../../shared/models/datacenter-aggrid-view';
import { Selection } from '../../../shared/models/selection';
import { TranslationService } from '../../../shared/services/translation.service';
import { CallableOrderItem } from '../../models/ecall-off-model';
import { ECallOffByVolumeViewService } from '../../services/ecall-off-by-volume-view-service';
import { SlideButtonComponent } from '../../../shared/components/slide-button/slide-button.component';
import { DatacenterToolHeaderComponent } from '../../../shared/datacenter/datacenter-tool-header.component';
import { ColDefWithFields } from '../../../shared/datacenter/column-type';
import { ContextDatacenter } from '../../../shared/datacenter/types/context-datacenter';
import { WorkerService } from '../../../shared/services/worker.service';

@Component({
	selector: 'copo-by-volume-order-item-list',
	templateUrl: './by-volume-order-item-list.component.html',
	styleUrl: './by-volume-order-item-list.component.css',
	imports: [DatacenterToolHeaderComponent, NgIf, SlideButtonComponent, AgGridModule]
})
export class ByVolumeOrderListComponent implements OnChanges, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	@Input() currentView?: UserDatacenterView;
	@Input() orderItems: CallableOrderItem[] = [];
	@Input() selectedOrderItems: CallableOrderItem[] = [];
	@Input() views: UserDatacenterView[] = [];
	@Output() readonly changeView = new EventEmitter<UserDatacenterView>(true);
	@Output() readonly customizeView = new EventEmitter<UserDatacenterView>(true);
	@Output() readonly displayOrder = new EventEmitter<CallableOrderItem>(true);
	@Output() readonly selectOrder = new EventEmitter<Selection<CallableOrderItem>>(true);

	gridApi?: GridApi<CallableOrderItem>;
	gridModules: Module[];
	gridOptions: CommonGridOptions<CallableOrderItem>;
	isAll: boolean;
	onResizeSubscription: Subscription;
	exportInProgress = false;
	datacenterType: DatacenterType;
	exportExcelName = 'Ecall-off-by-volume';
	contextDatacenter: ContextDatacenter;

	constructor(
		private readonly viewService: ECallOffByVolumeViewService,
		translationService: TranslationService,
		private readonly route: ActivatedRoute,
		readonly injector: Injector,
		readonly workerService: WorkerService
	) {
		this.datacenterType = <DatacenterType> this.route.snapshot.data['type'];
		this.contextDatacenter = new ContextDatacenter(this.datacenterType, this.injector);
		this.gridOptions = new CommonGridOptions<CallableOrderItem>({
			columnDefs: [],
			context: {
				onDisplay: (item: CallableOrderItem) => {
					this.displayOrder.emit(item);
				}
			},
			headerHeight: 70,
			loadingOverlayComponent: LoadingSpinnerCellComponent,
			multiSortKey: 'ctrl',
			rowData: [],
			rowSelection: 'multiple',
			suppressRowClickSelection: true,
			translationService
		});
		this.gridModules = clientSideAgGridModules;
		this.isAll = true;
		this.onResizeSubscription = fromEvent(globalThis, 'resize').subscribe(() => this.gridApi?.sizeColumnsToFit());
	}

	ngOnChanges(changes: SimpleChanges) {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (changes['orderItems']) {
			if (this.orderItems.length === 0) { // => destroy the grid
				this.gridApi = undefined;
			}
			this.updateGridData();
		}
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (changes['selectedOrderItems']) {
			this.setSelectedGridData(this.selectedOrderItems);
		}
	}

	ngOnDestroy(): void {
		this.onResizeSubscription.unsubscribe();
	}

	setAll(checked: boolean) {
		this.isAll = checked;
		this.updateGridData();
	}

	onViewChange(view: UserDatacenterView) {
		this.currentView = view;
		this.updateColumnDefinitions();
		this.updateFilters();
		this.changeView.emit(view);
	}

	onGridReady(params: GridReadyEvent<CallableOrderItem>) {
		this.gridApi = params.api;
		this.updateColumnDefinitions();
		this.updateFilters();
		this.updateGridData();
		this.setSelectedGridData(this.selectedOrderItems);
	}

	updateColumnDefinitions() {
		if (!this.gridApi || !this.currentView) {
			return;
		}
		const allColumns = <ColDefWithFields[]> this.viewService.getAllColumnDefinitions();
		const definitions = this.viewService.convertViewToColumnDefinitions(this.currentView, allColumns);
		this.gridApi.setGridOption('columnDefs', <ColDef<CallableOrderItem>[]> definitions);
		this.gridApi.applyColumnState({ state: definitions });
		this.gridApi.sizeColumnsToFit();
	}

	updateFilters() {
		if (!this.gridApi || !this.currentView) {
			return;
		}
		const newFilterModel = this.viewService.convertViewToFilterModel(this.currentView);
		this.gridApi.setFilterModel(newFilterModel ?? null);
		this.gridApi.onFilterChanged();
	}

	updateGridData() {
		if (!this.gridApi) {
			return;
		}
		const data = this.getVisibleGridData();
		const selectedData = this.getCurrentSelectedGridData();
		this.gridApi.setGridOption('rowData', data);
		this.setSelectedGridData(selectedData);
		this.gridApi.sizeColumnsToFit();
	}

	getVisibleGridData() {
		if (this.isAll) {
			return this.orderItems;
		}
		return this.getCurrentSelectedGridData();
	}

	getCurrentSelectedGridData() {
		if (!this.gridApi) {
			return [];
		}
		return this.gridApi.getSelectedNodes()
			.filter((node): node is (Omit<typeof node, 'data'> & { data: NonNullable<typeof node['data']> }) =>
				node.data !== undefined)
			.map(node => node.data);
	}

	setSelectedGridData(data: CallableOrderItem[]) {
		if (!this.gridApi) {
			return;
		}
		this.gridApi.forEachNode(node => {
			const isSelected = data.some(orderItem => orderItem.id && orderItem.id === node.data?.id);
			node.setSelected(isSelected);
		});
	}

	getDataToExport() {
		const filteredResults: unknown[] = [];
		if (this.selectedOrderItems.length > 0) {
			return this.selectedOrderItems;
		} else if (this.gridApi) {
			this.gridApi.forEachNodeAfterFilterAndSort(rowNode => {
				filteredResults.push(rowNode.data);
			});
			if (filteredResults.length > 0) {
				return filteredResults;
			}
		}
		return this.orderItems;
	}

	async exportExcelData() {
		if (this.gridApi?.getColumnDefs()) {
			this.exportInProgress = true;
			const allColumns = <ColDefWithFields[]> this.viewService.getAllColumnDefinitions();
			const defaultView = this.viewService.getDefaultView();
			this.currentView ??= this.viewService.getCurrentView(defaultView);
			const definitions =
			this.viewService.convertViewToColumnDefinitions(this.currentView, allColumns)
				.filter(col => col.colId !== 'add' && col.colId !== 'display' && !col.hide);
			const formatedData = this.formatExportedData(this.getDataToExport(), definitions);
			const columnTypes = definitions.map(columnDef => columnDef.type);
			await this.exportToExcel(formatedData, columnTypes);
		}
	}

	formatExportedData(dataToExport: unknown[], columnDefs: (ColDefWithFields & ColumnState)[]) {
		return [
			columnDefs.map(columnDef => columnDef.headerName ?? 'Error'),
			...dataToExport.map(
				data => columnDefs.map(columnDef => this.contextDatacenter.formatCellByType(columnDef, data))
			)
		];
	}

	async exportToExcel(formatedDataToExport: string[][], columnTypes: (string | string[] | undefined)[]) {
		return new Promise<void>((resolve, _reject) => {
			// if you change this line make sure that you change similar line inside gulp file
			const worker = this.workerService.createWorkerExcel();
			const fileName = `${ this.exportExcelName }-${ moment().format('YYYY MM DD') }.xlsx`;
			worker.addEventListener('message', ({ data }: { data: string }) => {
				this.exportInProgress = false;
				const link = document.createElement('a');
				link.download = fileName;
				link.href = data;
				link.append(document.createTextNode(fileName));
				document.body.append(link);
				link.click();
				link.remove();
				worker.terminate();
				resolve();
				// let 5 seconds to the browser for downloading before revoking the url
				setTimeout(() => {
					URL.revokeObjectURL(data);
				}, 5000);
			});
			const sheets = [
				{
					fileName,
					rows: formatedDataToExport,
					workSheetName: this.exportExcelName,
					columnTypes
				}
			];
			worker.postMessage({
				sheets
			});
		});
	}
}
